import type { AutoWithdrawalAmountMode, AutoWithdrawalNetworkCode } from '@shared/api/contracts';
import { getPairBalanceAmount } from './pair-balance';
import type { AssetBalanceDto } from '@shared/api/contracts';
import { SINGLE_USDT_NETWORKS } from '@entities/withdrawal';
import { parseNonNegativeDecimal } from '@shared/lib/parse-decimal';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';

/** Illustrative rates for UI preview (as in product prototype). Not billing source of truth. */
const SWAP_FEE_PERCENT = 0.5;
const NETWORK_FEE_USDT: Record<AutoWithdrawalNetworkCode, number> = {
  TRC20: 1,
  ERC20: 4.5,
  BEP20: 0.3,
  BTC: 0,
  LTC: 0,
};
const WITHDRAWAL_FEE_USDT = 1;

export interface SwapFeeEstimateLine {
  labelKey: 'withdrawalAmount' | 'conversion' | 'network' | 'withdrawal';
  labelParams?: Record<string, string>;
  value: string;
  negative?: boolean;
}

export interface SwapFeeEstimate {
  lines: SwapFeeEstimateLine[];
  netReceive: string;
}

const parseAmountNumber = (value: string): number => {
  const parsed = parseNonNegativeDecimal(value);
  if (!parsed.valid) return 0;
  return Number(parsed.normalized);
};

export const estimateSwapModeFees = (params: {
  assetBalances: AssetBalanceDto[];
  totalBalance?: number;
  targetNetwork: AutoWithdrawalNetworkCode;
  thresholdAmount: string;
  amountMode: AutoWithdrawalAmountMode;
  reserveAmount: string;
}): SwapFeeEstimate | null => {
  const total =
    params.totalBalance ??
    SINGLE_USDT_NETWORKS.reduce(
      (sum, network) => sum + parseAmountNumber(getPairBalanceAmount(params.assetBalances, 'USDT', network)),
      0,
    );

  if (total <= 0) {
    return null;
  }

  const keep =
    params.amountMode === 'KEEP_RESERVE' ? parseAmountNumber(params.reserveAmount) : 0;
  const withdrawAmount = Math.max(total - keep, 0);
  if (withdrawAmount <= 0) return null;

  const ratio = total > 0 ? withdrawAmount / total : 0;
  const foreign = SINGLE_USDT_NETWORKS
    .filter((network) => network !== params.targetNetwork)
    .reduce(
      (sum, network) =>
        sum + parseAmountNumber(getPairBalanceAmount(params.assetBalances, 'USDT', network)) * ratio,
      0,
    );

  const conversionFee = (foreign * SWAP_FEE_PERCENT) / 100;
  const networkFee = NETWORK_FEE_USDT[params.targetNetwork] ?? 0;
  const netReceive = Math.max(withdrawAmount - conversionFee - networkFee - WITHDRAWAL_FEE_USDT, 0);

  const fmt = (n: number) => formatLedgerAmount(n, { assetLabel: 'USDT' });

  return {
    lines: [
      {
        labelKey: 'withdrawalAmount',
        value: `${fmt(withdrawAmount)} USDT`,
      },
      {
        labelKey: 'conversion',
        labelParams: {
          percent: String(SWAP_FEE_PERCENT),
          foreign: fmt(foreign),
        },
        value: `− ${fmt(conversionFee)} USDT`,
        negative: true,
      },
      {
        labelKey: 'network',
        labelParams: { network: params.targetNetwork },
        value: `− ${fmt(networkFee)} USDT`,
        negative: true,
      },
      {
        labelKey: 'withdrawal',
        value: `− ${fmt(WITHDRAWAL_FEE_USDT)} USDT`,
        negative: true,
      },
    ],
    netReceive: `${fmt(netReceive)} USDT`,
  };
};
